/* eslint-disable no-loop-func */
import React, { useState, useEffect, useRef } from 'react';
import { ReactGrid, Column, Row, DefaultCellTypes, CellLocation, CellChange } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import './DataGrid.css';
import ColumnHeaderMenu, { ColumnMenuOptions } from './ColumnHeaderMenu';
import { textAreaCellTemplate, TextAreaCell } from './TextAreaCellTemplate';
import ColumnHoverButton from './ColumnHoverButton';
import RowToolbar from './RowToolbar';
import AddRowButton from './AddRowButton';
import { useUserStorage } from '../services/userDataStorage';
import { createCellChange, applyIncrementalGridChanges, CellChange as FirestoreCellChange } from '../services/firestoreService';
import { useUser } from '@clerk/clerk-react';
import { processRowData } from '../services/apiService';
import { getUserSubscriptionFromFirestore } from '../services/firestoreService';
import toast from 'react-hot-toast';

interface CellData {
  [key: string]: string | number;
}

interface ColumnConfig {
  id: string | number;
  name: string;
  options: ColumnMenuOptions;
  width: number;
  hidden?: boolean;
}

interface SelectedCellInfo {
  rowIndex: number;
  content: string;
  columnId: string | number;
}

interface DataGridProps {
  onCellSelected?: (cellInfo: SelectedCellInfo | null) => void;
  onAddColumn?: () => void;
  onSetUndoHandler?: (handler: () => void) => void;
  onSetRedoHandler?: (handler: () => void) => void;
  onUndoRedoStateChange?: (canUndo: boolean, canRedo: boolean) => void;
  activeGridId?: string;
  onTitleChange?: (title: string) => void;
  onDataReady?: () => void;
  onSelectionChanged?: (selectedRowsCount: number, selectedRows: CellData[]) => void;
  onColumnConfigsChanged?: (columnConfigs: ColumnConfig[]) => void;
  onSetUpdateHandler?: (handler: (rowId: string | number, updatedData: Record<string, any>) => void) => void;
  onSetDeselectHandler?: (handler: () => void) => void;
  onSubscriptionRefresh?: () => void;
}

const DataGrid: React.FC<DataGridProps> = ({ 
  onCellSelected, 
  onAddColumn,
  onSetUndoHandler,
  onSetRedoHandler,
  onUndoRedoStateChange,
  activeGridId,
  onTitleChange,
  onDataReady,
  onSelectionChanged,
  onColumnConfigsChanged,
  onSetUpdateHandler,
  onSetDeselectHandler,
  onSubscriptionRefresh
}) => {
  const userStorage = useUserStorage();
  const { user } = useUser();
  
  // Initialize column configurations with empty names
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(
    Array.from({ length: 10 }, (_, index) => ({
      id: index,
      name: '', // Start with empty names
      options: {},
      width: 200,
    }))
  );

  // Add state for selected cell tracking
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);
  const [rangeSelectionCount] = useState(0);
  const [editingCell, setEditingCell] = useState<SelectedCellInfo | null>(null);
  
  const selectedCellRef = useRef<SelectedCellInfo | null>(null);
  const gridRef = useRef<any>(null);
  const [currentGridId, setCurrentGridId] = useState<string | null>(null);
  const [gridTitle, setGridTitle] = useState<string>("ReactGrid Sheet");

  // Enrichment state - track enriching cells/columns individually
  const [enrichingCells, setEnrichingCells] = useState<Set<string>>(new Set());
  const [enrichingColumns, setEnrichingColumns] = useState<Set<string | number>>(new Set());
  const [hoveredColumnId, setHoveredColumnId] = useState<string | number | null>(null);

  // Helper functions for enrichment state
  const isCellEnriching = (rowId: number, columnId: string | number): boolean => {
    return enrichingCells.has(`${rowId}-${columnId}`);
  };

  const isColumnEnriching = (columnId: string | number): boolean => {
    return enrichingColumns.has(columnId);
  };

  const setCellEnriching = (rowId: number, columnId: string | number, enriching: boolean) => {
    const cellKey = `${rowId}-${columnId}`;
    setEnrichingCells(prev => {
      const newSet = new Set(prev);
      if (enriching) {
        newSet.add(cellKey);
      } else {
        newSet.delete(cellKey);
      }
      return newSet;
    });
  };

  const setColumnEnriching = (columnId: string | number, enriching: boolean) => {
    setEnrichingColumns(prev => {
      const newSet = new Set(prev);
      if (enriching) {
        newSet.add(columnId);
      } else {
        newSet.delete(columnId);
      }
      return newSet;
    });
  };

  // Update ref when selectedCell changes
  useEffect(() => {
    selectedCellRef.current = selectedCell;
  }, [selectedCell]);

  // Create empty grid data - 50 rows with dynamic columns
  const createEmptyData = (): CellData[] => {
    return Array.from({ length: 50 }, (_, index) => {
      const row: CellData = { id: index + 1 };
      columnConfigs.forEach(config => {
        if (!config.hidden) {
          row[config.id] = '';
        }
      });
      return row;
    });
  };

  const [gridData, setGridData] = useState<CellData[]>(createEmptyData());

  // Enrichment helper functions
  const hasAtLeastOneNonEmptyCell = (row: any): boolean => {
    return Object.keys(row).some(key => {
      if (key === 'id') return false;
      const value = row[key];
      return value !== '' && value !== null && value !== undefined;
    });
  };

  const hasEmptyCellWithHeaderName = (row: any, columnId?: string | number): boolean => {
    if (columnId) {
      // Check specific column
      const config = columnConfigs.find(c => c.id === columnId);
      if (!config) return false;
      const hasNonEmptyHeader = Boolean(config.name && config.name.trim() !== '');
      const hasEmptyCell = !row[config.id] || row[config.id] === '' || row[config.id] === null || row[config.id] === undefined;
      return hasNonEmptyHeader && hasEmptyCell;
    } else {
      // Check any column
      return columnConfigs.some(config => {
        const hasNonEmptyHeader = Boolean(config.name && config.name.trim() !== '');
        const hasEmptyCell = !row[config.id] || row[config.id] === '' || row[config.id] === null || row[config.id] === undefined;
        return hasNonEmptyHeader && hasEmptyCell;
      });
    }
  };

  // Single cell enrichment
  const handleCellEnrich = async (rowIndex: number, columnId: string | number) => {
    if (isCellEnriching(rowIndex, columnId) || !user?.id) return;

    const row = gridData.find(r => r.id === rowIndex);
    if (!row) return;

    const config = columnConfigs.find(c => c.id === columnId);
    if (!config || !config.name || config.name.trim() === '') {
      toast.error('Column must have a name to be enriched');
      return;
    }

    // Check if row has data and cell is empty
    if (!hasAtLeastOneNonEmptyCell(row)) {
      toast.error('Row must have some data to enrich other cells');
      return;
    }

    if (!hasEmptyCellWithHeaderName(row, columnId)) {
      toast.error('Cell is already filled');
      return;
    }

    // Check credits
    try {
      const subscription = await getUserSubscriptionFromFirestore(user.id);
      if (!subscription || subscription.credits < 1) {
        toast.error('Not enough credits for enrichment');
        return;
      }
    } catch (error) {
      toast.error('Unable to verify credit balance');
      return;
    }

    setCellEnriching(rowIndex, columnId, true);

    try {
      // Mark cell as generating in database
      if (currentGridId) {
        await applyIncrementalGridChanges(currentGridId, user.id, {
          cellChanges: [createCellChange(
            gridData.findIndex(row => row.id === rowIndex),
            String(columnId),
            'Generating...',
            String(row[columnId] || '')
          )]
        });
      }

      // Also update local state immediately for responsiveness
      updateRowData(rowIndex, { [columnId]: 'Generating...' });

      // Prepare row data for API
      const rowData: any = { 
        id: rowIndex,
        userId: user.id,
        gridId: currentGridId,
        rowId: rowIndex,
        columnId: columnId,
      };
      
      columnConfigs.forEach(config => {
        const cellContent = row[config.id];
        
        if (cellContent && cellContent !== 'Generating...' && cellContent !== 'Queued...') {
          rowData[`col${config.id}`] = {
            content: cellContent,
            headerName: config.name || '',
            promptDetails: config.options?.promptDetails || '',
            formatOptions: config.options?.formatDetails || ''
          };
        } else if (config.id === columnId) {
          rowData[`col${config.id}`] = {
            content: '',
            headerName: config.name,
            promptDetails: config.options?.promptDetails || '',
            formatOptions: config.options?.formatDetails || ''
          };
        }
      });

      // Call enrichment API
      const response = await processRowData(rowData);
      
      if (response.success) {
        // Check if we should refresh from database
        if (response.data?.shouldRefreshFromDB) {
          // Webhook processed the data and updated the database
          // Refresh the entire grid from the database
          
          if (currentGridId && userStorage) {
            try {
              
              // Clear cache to force fresh load from database
              userStorage.clearCache();
              
              const refreshedGrid = await userStorage.loadGrid(currentGridId);
              
              if (refreshedGrid) {
                
                setGridData(refreshedGrid.gridData || createEmptyData());
                setColumnConfigs(refreshedGrid.columnConfigs || columnConfigs);
                
                // Deduct credit for successful processing
                if (onSubscriptionRefresh) {
                  onSubscriptionRefresh();
                }
                
                toast.success('Cell enriched successfully');
              } else {
                console.warn('⚠️ No refreshed grid data received');
                updateRowData(rowIndex, { [columnId]: '' });
                toast.error('No data received from database');
              }
            } catch (refreshError) {
              console.error('❌ Error refreshing grid from database:', refreshError);
              updateRowData(rowIndex, { [columnId]: '' });
              toast.error('Failed to refresh data from database');
            }
          } else {
            console.error('❌ Missing currentGridId or userStorage for refresh');
            updateRowData(rowIndex, { [columnId]: '' });
            toast.error('Cannot refresh: missing grid ID or storage');
          }
        } else {
          // Handle legacy response format (fallback)
          const filledColumns = response.data?.webhookResponse?.filledColumns || response.data?.processedData?.filledColumns;
          const colKey = `col${columnId}`;
          
          if (filledColumns && filledColumns[colKey]) {
            const content = filledColumns[colKey].content;
            updateRowData(rowIndex, { [columnId]: content });
            
            // Deduct credit if content was generated
            if (content && content.trim() !== '') {
              if (onSubscriptionRefresh) {
                onSubscriptionRefresh();
              }
            }
          } else {
            updateRowData(rowIndex, { [columnId]: '' });
          }
        }
      } else {
        updateRowData(rowIndex, { [columnId]: '' });
        toast.error(response.data?.error || 'Failed to enrich cell');
      }
    } catch (error) {
      console.error('Cell enrichment error:', error);
      updateRowData(rowIndex, { [columnId]: '' });
      toast.error('Failed to enrich cell');
    } finally {
      setCellEnriching(rowIndex, columnId, false);
    }
  };

  // Column enrichment
  const handleColumnEnrich = async (columnId: string | number, rowCount: number | 'all') => {
    if (isColumnEnriching(columnId) || !user?.id) return;

    const config = columnConfigs.find(c => c.id === columnId);
    if (!config || !config.name || config.name.trim() === '') {
      toast.error('Column must have a name to be enriched');
      return;
    }

    // Get eligible rows
    let eligibleRows = gridData.filter(row => {
      const hasData = hasAtLeastOneNonEmptyCell(row);
      const hasEmptyCell = hasEmptyCellWithHeaderName(row, columnId);
      return hasData && hasEmptyCell;
    });

    if (eligibleRows.length === 0) {
      toast.error('No eligible rows found for enrichment');
      return;
    }

    // Limit rows based on selection
    if (rowCount !== 'all') {
      eligibleRows = eligibleRows.slice(0, rowCount);
    }

    // Check credits
    try {
      const subscription = await getUserSubscriptionFromFirestore(user.id);
      if (!subscription || subscription.credits < eligibleRows.length) {
        toast.error(`Not enough credits. Need ${eligibleRows.length}, have ${subscription?.credits || 0}`);
        return;
      }
    } catch (error) {
      toast.error('Unable to verify credit balance');
      return;
    }

    setColumnEnriching(columnId, true);
    toast(`Enriching ${eligibleRows.length} rows in ${config.name} column...`);

    try {
      let creditsUsed = 0;

      // Process rows in batches
      const BATCH_SIZE = 10;
      for (let i = 0; i < eligibleRows.length; i += BATCH_SIZE) {
        const batch = eligibleRows.slice(i, i + BATCH_SIZE);
        
        // Mark cells as generating
        batch.forEach(row => {
          updateRowData(row.id, { [columnId]: 'Generating...' });
        });

        // Also save "Generating..." to database for persistence
        if (currentGridId) {
          const cellChanges = batch.map(row => createCellChange(
            gridData.findIndex(gridRow => gridRow.id === row.id),
            String(columnId),
            'Generating...',
            String(row[columnId] || '')
          ));
          
          await applyIncrementalGridChanges(currentGridId, user.id, {
            cellChanges
          });
        }

        // Process batch
        const batchPromises = batch.map(async (row) => {
          try {
            // Prepare row data for API
            const rowData: any = { 
              id: row.id,
              userId: user.id,
              gridId: currentGridId,
              rowId: row.id,
              columnId: columnId
            };
            
            columnConfigs.forEach(config => {
              const cellContent = row[config.id];
              
              if (cellContent && cellContent !== 'Generating...' && cellContent !== 'Queued...') {
                rowData[`col${config.id}`] = {
                  content: cellContent,
                  headerName: config.name || '',
                  promptDetails: config.options?.promptDetails || '',
                  formatOptions: config.options?.formatDetails || ''
                };
              } else if (config.id === columnId) {
                rowData[`col${config.id}`] = {
                  content: '',
                  headerName: config.name,
                  promptDetails: config.options?.promptDetails || '',
                  formatOptions: config.options?.formatDetails || ''
                };
              }
            });

            // Call enrichment API
            const response = await processRowData(rowData);
            
            if (response.success) {
              // Check if we should refresh from database
              if (response.data?.shouldRefreshFromDB) {
                // Webhook processed the data and updated the database
                // For column enrichment, we'll track credits but refresh at the end
                creditsUsed += 1;
              } else {
                // Handle legacy response format (fallback)
                const filledColumns = response.data?.webhookResponse?.filledColumns || response.data?.processedData?.filledColumns;
                const colKey = `col${columnId}`;
                
                if (filledColumns && filledColumns[colKey]) {
                  const content = filledColumns[colKey].content;
                  updateRowData(row.id, { [columnId]: content });
                  
                  if (content && content.trim() !== '') {
                    creditsUsed += 1;
                  }
                } else {
                  updateRowData(row.id, { [columnId]: '' });
                }
              }
            } else {
              updateRowData(row.id, { [columnId]: '' });
            }
          } catch (error) {
            console.error(`Error enriching row ${row.id}:`, error);
            updateRowData(row.id, { [columnId]: '' });
          }
        });

        await Promise.all(batchPromises);
        
        // Small delay between batches
        if (i + BATCH_SIZE < eligibleRows.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Deduct credits
      if (creditsUsed > 0) {
        if (onSubscriptionRefresh) {
          onSubscriptionRefresh();
        }
      }

      // Refresh grid from database if webhook processing was used
      if (creditsUsed > 0 && currentGridId && userStorage) {
        try {
          
          // Clear cache to force fresh load from database
          userStorage.clearCache();
          
          const refreshedGrid = await userStorage.loadGrid(currentGridId);
          
          if (refreshedGrid) {
            
            setGridData(refreshedGrid.gridData || createEmptyData());
            setColumnConfigs(refreshedGrid.columnConfigs || columnConfigs);
          } else {
            console.warn('⚠️ No refreshed grid data received after column enrichment');
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing grid from database after column enrichment:', refreshError);
        }
      }

      toast.success(`Successfully enriched ${creditsUsed} cells`);
    } catch (error) {
      console.error('Column enrichment error:', error);
      toast.error('Failed to enrich column');
    } finally {
      setColumnEnriching(columnId, false);
    }
  };

  // Load grid data when activeGridId changes
  useEffect(() => {
    const loadGrid = async () => {
      if (!userStorage?.isFirebaseReady) {
        return;
      }

      if (!activeGridId) {
        if (onDataReady) {
          setTimeout(() => {
            onDataReady();
          }, 0);
        }
        return;
      }

      try {
        const loadedGrid = await userStorage.loadGrid(activeGridId);
        if (loadedGrid) {
          // Don't clean "Generating..." states - let them persist
          setGridData(loadedGrid.gridData || createEmptyData());
          setColumnConfigs(loadedGrid.columnConfigs || columnConfigs);
          setGridTitle(loadedGrid.title);
          setCurrentGridId(loadedGrid.id);
          
          // Update parent title
          if (onTitleChange) {
            onTitleChange(loadedGrid.title);
          }
        }
        
        // Always notify ready after processing (success or no data found)
        if (onDataReady) {
          setTimeout(() => {
            onDataReady();
          }, 0);
        }
      } catch (error) {
        console.error('Failed to load grid:', error);
        // Even on error, notify ready to show empty grid
        if (onDataReady) {
          setTimeout(() => {
            onDataReady();
          }, 0);
        }
      }
    };

    loadGrid();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGridId, userStorage?.isFirebaseReady]);

  // State for tracking pending changes to batch them
  const [pendingCellChanges, setPendingCellChanges] = useState<FirestoreCellChange[]>([]);
  const [pendingColumnConfigChanges, setPendingColumnConfigChanges] = useState<any[] | null>(null);
  const [pendingTitleChange, setPendingTitleChange] = useState<string | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced save function for incremental updates
  const saveIncrementalChanges = async () => {
    if (!currentGridId || !user?.id || !userStorage?.isFirebaseReady) {
        return;
      }

      try {
      // Apply all pending changes in a single batch
      if (pendingCellChanges.length > 0 || pendingColumnConfigChanges || pendingTitleChange) {
        await applyIncrementalGridChanges(currentGridId, user.id, {
          cellChanges: pendingCellChanges,
          columnConfigChanges: pendingColumnConfigChanges || undefined,
          title: pendingTitleChange || undefined
        });

        // Clear pending changes after successful save
        setPendingCellChanges([]);
        setPendingColumnConfigChanges(null);
        setPendingTitleChange(null);
        
      }
      } catch (error) {
      console.error('Failed to save incremental changes:', error);
    }
  };

  // Effect to handle debounced saving of incremental changes
  useEffect(() => {
    if (pendingCellChanges.length > 0 || pendingColumnConfigChanges || pendingTitleChange) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for debounced save
      saveTimeoutRef.current = setTimeout(saveIncrementalChanges, 500);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCellChanges, pendingColumnConfigChanges, pendingTitleChange, currentGridId, user?.id, userStorage?.isFirebaseReady]);

  // Track title changes for incremental save
  const prevGridTitle = useRef<string>(gridTitle);
  useEffect(() => {
    if (currentGridId && prevGridTitle.current !== gridTitle && prevGridTitle.current !== "ReactGrid Sheet") {
      // Title changed, mark for incremental save
      setPendingTitleChange(gridTitle);
    }
    prevGridTitle.current = gridTitle;
  }, [gridTitle, currentGridId]);

  // Track column config changes for incremental save
  const prevColumnConfigs = useRef<ColumnConfig[]>(columnConfigs);
  useEffect(() => {
    if (currentGridId && JSON.stringify(prevColumnConfigs.current) !== JSON.stringify(columnConfigs)) {
      // Column configs changed, mark for incremental save
      setPendingColumnConfigChanges(columnConfigs);
    }
    prevColumnConfigs.current = columnConfigs;
  }, [columnConfigs, currentGridId]);

  // Update row data with enriched content
  const updateRowData = (rowId: string | number, updatedData: Record<string, any>) => {
    
    setGridData(prevData => {
      const newData = [...prevData];
      const rowIndex = newData.findIndex(row => row.id === rowId);
      
      if (rowIndex !== -1) {
        // Update the row with new data
        newData[rowIndex] = {
          ...newData[rowIndex],
          ...updatedData
        };
      } else {
        console.warn('Row not found with id:', rowId);
      }
      
      return newData;
    });
  };

  // Register the update handler with parent component
  useEffect(() => {
    if (onSetUpdateHandler) {
      onSetUpdateHandler(updateRowData);
    }
  }, [onSetUpdateHandler]);

  // Register the deselect handler with parent component
  useEffect(() => {
    if (onSetDeselectHandler) {
      onSetDeselectHandler(handleDeselectAll);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSetDeselectHandler]);

  // Undo/Redo state management
  const [cellChangesHistory, setCellChangesHistory] = useState<CellChange[][]>([]);
  const [cellChangesIndex, setCellChangesIndex] = useState(-1);

  // Helper function to apply cell changes to grid data
  const applyNewValue = (
    changes: CellChange[],
    prevData: CellData[],
    usePrevValue: boolean = false
  ): CellData[] => {
    const newData = [...prevData];
    changes.forEach((change) => {
      if (change.rowId !== 'header' && change.columnId !== 'id') {
        const cell = usePrevValue ? change.previousCell : change.newCell;
        const rowData = newData.find(row => row.id === change.rowId);
        if (rowData && 'text' in cell) {
          rowData[change.columnId] = (cell as any).text || '';
        }
      }
    });
    return newData;
  };

  // Function to apply changes and update history
  const applyChangesToData = (
    changes: CellChange[],
    prevData: CellData[]
  ): CellData[] => {
    const updated = applyNewValue(changes, prevData);
    setCellChangesHistory([...cellChangesHistory.slice(0, cellChangesIndex + 1), changes]);
    setCellChangesIndex(cellChangesIndex + 1);
    return updated;
  };

  // Undo function
  const undoChanges = (
    changes: CellChange[],
    prevData: CellData[]
  ): CellData[] => {
    const updated = applyNewValue(changes, prevData, true);
    setCellChangesIndex(cellChangesIndex - 1);
    return updated;
  };

  // Redo function
  const redoChanges = (
    changes: CellChange[],
    prevData: CellData[]
  ): CellData[] => {
    const updated = applyNewValue(changes, prevData);
    setCellChangesIndex(cellChangesIndex + 1);
    return updated;
  };

  // Handle undo operation
  const handleUndoChanges = () => {
    if (cellChangesIndex >= 0) {
      setGridData((prevData) =>
        undoChanges(cellChangesHistory[cellChangesIndex], prevData)
      );
    }
  };

  // Handle redo operation
  const handleRedoChanges = () => {
    if (cellChangesIndex + 1 <= cellChangesHistory.length - 1) {
      setGridData((prevData) =>
        redoChanges(cellChangesHistory[cellChangesIndex + 1], prevData)
      );
    }
  };

  // Notify parent component of undo/redo handlers and state
  useEffect(() => {
    if (onSetUndoHandler) {
      // Replace the onSetUndoHandler callback with our handler
      onSetUndoHandler(handleUndoChanges);
    }
    if (onSetRedoHandler) {
      // Replace the onSetRedoHandler callback with our handler  
      onSetRedoHandler(handleRedoChanges);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellChangesIndex, cellChangesHistory, onSetUndoHandler, onSetRedoHandler]);

  // Notify parent of undo/redo state changes
  useEffect(() => {
    if (onUndoRedoStateChange) {
      const canUndo = cellChangesIndex >= 0;
      const canRedo = cellChangesIndex + 1 <= cellChangesHistory.length - 1;
      onUndoRedoStateChange(canUndo, canRedo);
    }
  }, [cellChangesIndex, cellChangesHistory, onUndoRedoStateChange]);

  // Console log when selected rows change
  useEffect(() => {
    // Only keep essential logging if needed for debugging
  }, [selectedCell, gridData]);

  // Call selection changed callback when selection changes (simplified)
  useEffect(() => {
    if (onSelectionChanged) {
      onSelectionChanged(0, []); // No selection functionality
    }
  }, [onSelectionChanged]);

  // Call column configs changed callback when column configs change
  useEffect(() => {
    if (onColumnConfigsChanged) {
      onColumnConfigsChanged(columnConfigs);
    }
  }, [columnConfigs, onColumnConfigsChanged]);

  // Real-time change handler for editing cells
  const handleRealTimeChange = (rowId: string | number, columnId: string | number, newContent: string) => {
    // When real-time change happens, this cell is being edited
    const editingCellInfo: SelectedCellInfo = {
      rowIndex: Number(rowId),
      content: newContent,
      columnId: columnId
    };
    
    // Set this as the editing cell
    setEditingCell(editingCellInfo);
    
    // Also update selected cell to match the editing cell
    setSelectedCell(editingCellInfo);
    if (onCellSelected) {
      onCellSelected(editingCellInfo);
    }
  };

  // Add column functionality
  const handleAddColumn = () => {
    const newColumnId = Math.max(...columnConfigs.map(c => Number(c.id))) + 1;
    const newColumn: ColumnConfig = {
      id: newColumnId,
      name: '', // Will be set when user types in header
      options: {},
      width: 200,
    };

    // Add the new column to configs
    const updatedConfigs = [...columnConfigs, newColumn];
    setColumnConfigs(updatedConfigs);

    // Add empty values for the new column in all rows
    const updatedData = gridData.map(row => ({
      ...row,
      [newColumnId]: ''
    }));
    setGridData(updatedData);

    if (onAddColumn) {
      onAddColumn();
    }

    // Use the updated configs directly for scrolling
    setTimeout(() => {
      scrollToNewColumn(newColumnId, updatedConfigs);
    }, 100);
  };

  // Add rows functionality
  const handleAddRows = (count: number) => {
    setGridData(prevData => {
      const lastRowId = Math.max(...prevData.map(row => Number(row.id)));
      const newRows: CellData[] = Array.from({ length: count }, (_, index) => {
        const newRowId = lastRowId + index + 1;
        const row: CellData = { id: newRowId };
        
        // Add empty values for all visible columns
        columnConfigs.forEach(config => {
          if (!config.hidden) {
            row[config.id] = '';
          }
        });
        
        return row;
      });
      
      const updatedData = [...prevData, ...newRows];
      
      // Scroll to the last row after the grid updates
      setTimeout(() => {
        scrollToLastRow();
      }, 100);
      
      return updatedData;
    });
  };

  // Helper function to scroll to the last row
  const scrollToLastRow = () => {
    if (!gridRef.current) return;

    const gridElement = gridRef.current;
    const gridContainer = gridElement.parentElement; // This should be the data-grid-content div
    
    if (gridContainer) {
      // Scroll to the bottom of the container
      gridContainer.scrollTo({
        top: gridContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Helper function to scroll to a specific column
  const scrollToNewColumn = (columnId: string | number, configs?: ColumnConfig[]) => {
    if (!gridRef.current) {
      return;
    }

    const gridElement = gridRef.current;
    
    // Use provided configs or fall back to current state
    const configsToUse = configs || columnConfigs;
    const visibleColumns = configsToUse.filter(config => !config.hidden);
    const columnIndex = visibleColumns.findIndex(config => config.id === columnId);
    
    if (columnIndex === -1) {
      return;
    }

    // Calculate scroll position: ID column (50px) + sum of previous column widths
    const scrollPosition = 50 + visibleColumns.slice(0, columnIndex).reduce((sum, col) => sum + col.width, 0);

    // Try to find a scrollable container
    const gridContainer = gridElement.parentElement; // This should be the data-grid-content div
    
    if (gridContainer && gridContainer.scrollWidth > gridContainer.clientWidth) {
      gridContainer.scrollTo({
        left: scrollPosition,
        top: 0, // Scroll to top of the grid
        behavior: 'smooth'
      });
    } else {
      // Fallback: try scrolling the grid element itself
      if (gridElement.scrollWidth > gridElement.clientWidth) {
        gridElement.scrollLeft = scrollPosition;
        gridElement.scrollTop = 0; // Scroll to top
      }
      
      // Also try to scroll the container to top if it exists
      if (gridContainer) {
        gridContainer.scrollTop = 0;
      }
    }

    // After scrolling, try to focus the header cell
    setTimeout(() => {
      focusNewColumnHeader(columnId, configsToUse);
    }, 200);
  };

  // Helper function to focus the new column header
  const focusNewColumnHeader = (columnId: string | number, configs?: ColumnConfig[]) => {
    if (!gridRef.current) {
      return;
    }

    const gridElement = gridRef.current;
    
    // Find the column index in visible columns
    const visibleColumns = configs?.filter(config => !config.hidden) || columnConfigs.filter(config => !config.hidden);
    const columnIndex = visibleColumns.findIndex(config => config.id === columnId);
    
    if (columnIndex === -1) {
      return;
    }

    // Method 1: Try to focus the column header overlay input directly
    const overlayItems = document.querySelectorAll('.column-header-overlay-item');
    
    const targetOverlay = overlayItems[columnIndex] as HTMLElement;
    
    if (targetOverlay) {
      // Click the column header container to potentially open the menu
      const headerContainer = targetOverlay.querySelector('.column-header-container') as HTMLElement;
      if (headerContainer) {
        headerContainer.click();
        
        // Wait a bit and then try to focus the input
        setTimeout(() => {
          const input = targetOverlay.querySelector('.column-name-input') as HTMLInputElement;
          if (input) {
            input.focus();
            input.select(); // Select all text for easy replacement
            return; // Success, we're done
          } else {
          }
        }, 100);
      } else {
      }
    }

    // Method 2: Fallback - try to focus the ReactGrid header cell
    setTimeout(() => {
      const headerCells = gridElement.querySelectorAll('.rg-cell.rg-header-cell');
      
      if (columnIndex >= 0 && headerCells[columnIndex + 1]) { // +1 because ID column comes first
        const targetHeaderCell = headerCells[columnIndex + 1] as HTMLElement;
        targetHeaderCell.click();
        targetHeaderCell.focus();
      } else {
      }
    }, 200);
  };

  // Simplified deselect handler (for compatibility)
  const handleDeselectAll = () => {
    // Clear selected cell
    setSelectedCell(null);
    setEditingCell(null);
    if (onCellSelected) {
      onCellSelected(null);
    }
  };

  // Handle cell focus to track selected cell
  const handleCellFocus = React.useCallback((cellLocation: CellLocation) => {
    if (cellLocation.rowId === 'header' || cellLocation.columnId === 'id') {
      // Don't track header cells or ID column
      setSelectedCell(null);
      setEditingCell(null);
      if (onCellSelected) {
        onCellSelected(null);
      }
      return;
    }

    // Use setTimeout to ensure ReactGrid's focus change is fully processed
    setTimeout(() => {
      // Find the row data - rowId can be string or number
      const rowData = gridData.find(row => row.id === cellLocation.rowId);
      if (rowData) {
        const cellInfo: SelectedCellInfo = {
          rowIndex: Number(rowData.id),
          content: String(rowData[cellLocation.columnId] || ''),
          columnId: cellLocation.columnId
        };
        
        setSelectedCell(cellInfo);
        
        // Always clear editing state when focus changes to a different cell
        const wasEditing = editingCell !== null;
        if (wasEditing) {
          setEditingCell(null);
        }
        
        if (onCellSelected) {
          onCellSelected(cellInfo);
        }
      }
    }, 0);
  }, [gridData, onCellSelected, editingCell]);

  // Sync selected cell content with grid data changes
  useEffect(() => {
    // If there's an editing cell, that takes priority
    if (editingCell) {
      const rowData = gridData.find(row => row.id === editingCell.rowIndex);
      if (rowData) {
        const currentContent = String(rowData[editingCell.columnId] || '');
        // Only update if the content in the grid data is different from editing cell
        // This handles cases where grid data is updated externally
        if (currentContent !== editingCell.content && editingCell.content === '') {
          const updatedCellInfo: SelectedCellInfo = {
            ...editingCell,
            content: currentContent
          };
          setEditingCell(updatedCellInfo);
          setSelectedCell(updatedCellInfo);
          if (onCellSelected) {
            onCellSelected(updatedCellInfo);
          }
        }
      }
      return; // Don't sync regular selected cell when editing
    }
    
    // Regular sync for non-editing selected cell
    const currentSelectedCell = selectedCellRef.current;
    if (currentSelectedCell) {
      const rowData = gridData.find(row => row.id === currentSelectedCell.rowIndex);
      if (rowData) {
        const currentContent = String(rowData[currentSelectedCell.columnId] || '');
        if (currentContent !== currentSelectedCell.content) {
          const updatedCellInfo: SelectedCellInfo = {
            ...currentSelectedCell,
            content: currentContent
          };
          setSelectedCell(updatedCellInfo);
          if (onCellSelected) {
            onCellSelected(updatedCellInfo);
          }
        }
      }
    }
  }, [gridData, editingCell, onCellSelected]);

  // Console logging for grid configuration changes
  useEffect(() => {
    // Removed verbose logging
  }, [columnConfigs, gridData]);

  // Additional logging for specific configuration aspects
  useEffect(() => {
    // Removed verbose logging
  }, [columnConfigs]);

  // Define columns: ID column + dynamic data columns
  const getColumns = (): Column[] => {
    const visibleColumns = columnConfigs.filter(config => !config.hidden);
    return [
      { columnId: 'id', width: 50 }, // ID column (not resizable)
      ...visibleColumns.map(config => ({
        columnId: config.id,
        width: config.width,
        resizable: true, // Make data columns resizable
      })),
    ];
  };

  // Sort data based on column options
  const getSortedData = (data: CellData[]): CellData[] => {
    const sortedColumn = columnConfigs.find(config => config.options.sortDirection);
    if (!sortedColumn) return data;

    return [...data].sort((a, b) => {
      const aValue = String(a[sortedColumn.id] || '').trim();
      const bValue = String(b[sortedColumn.id] || '').trim();
      
      // Check if values are empty
      const aIsEmpty = aValue === '';
      const bIsEmpty = bValue === '';
      
      // If both are empty, maintain original order
      if (aIsEmpty && bIsEmpty) {
        return 0;
      }
      
      // Always put empty values at the end, regardless of sort direction
      if (aIsEmpty) return 1;  // a goes after b
      if (bIsEmpty) return -1; // b goes after a
      
      // Both values are non-empty, sort normally
      if (sortedColumn.options.sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  // Column menu handlers
  const handleColumnNameChange = (columnId: string | number, name: string) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, name }
          : config
      )
    );
  };

  const handlePromptDetailsChange = (columnId: string | number, details: string) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, promptDetails: details } }
          : config
      )
    );
  };

  const handleFormatDetailsChange = (columnId: string | number, details: string) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, formatDetails: details } }
          : config
      )
    );
  };

  const handleTypeChange = (columnId: string | number, type: string) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, type } }
          : config
      )
    );
  };

  const handleSortAscending = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => ({
        ...config,
        options: {
          ...config.options,
          sortDirection: config.id === columnId ? 'asc' : null
        }
      }))
    );
  };

  const handleSortDescending = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => ({
        ...config,
        options: {
          ...config.options,
          sortDirection: config.id === columnId ? 'desc' : null
        }
      }))
    );
  };

  const handleClearSort = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, sortDirection: null } }
          : config
      )
    );
  };

  const handleWrapText = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, isWrapped: !config.options.isWrapped } }
          : config
      )
    );
  };

  const handlePinColumn = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, options: { ...config.options, isPinned: !config.options.isPinned } }
          : config
      )
    );
  };

  const handleHideColumn = (columnId: string | number) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, hidden: true }
          : config
      )
    );
  };

  // Insert column to the left of the specified column
  const handleInsertColumnLeft = (columnId: string | number) => {
    const targetIndex = columnConfigs.findIndex(config => config.id === columnId);
    if (targetIndex === -1) return;

    const newColumnId = Math.max(...columnConfigs.map(c => Number(c.id))) + 1;
    const newColumn: ColumnConfig = {
      id: newColumnId,
      name: '',
      options: {},
      width: 200,
    };

    // Insert the new column at the target index (left of target)
    const updatedConfigs = [
      ...columnConfigs.slice(0, targetIndex),
      newColumn,
      ...columnConfigs.slice(targetIndex)
    ];
    setColumnConfigs(updatedConfigs);

    // Add empty values for the new column in all rows
    const updatedData = gridData.map(row => ({
      ...row,
      [newColumnId]: ''
    }));
    setGridData(updatedData);

    // Open the menu of the newly created column
    setTimeout(() => {
      openNewColumnMenu(newColumnId, updatedConfigs);
    }, 100);
  };

  // Insert column to the right of the specified column
  const handleInsertColumnRight = (columnId: string | number) => {
    const targetIndex = columnConfigs.findIndex(config => config.id === columnId);
    if (targetIndex === -1) return;

    const newColumnId = Math.max(...columnConfigs.map(c => Number(c.id))) + 1;
    const newColumn: ColumnConfig = {
      id: newColumnId,
      name: '',
      options: {},
      width: 200,
    };

    // Insert the new column after the target index (right of target)
    const updatedConfigs = [
      ...columnConfigs.slice(0, targetIndex + 1),
      newColumn,
      ...columnConfigs.slice(targetIndex + 1)
    ];
    setColumnConfigs(updatedConfigs);

    // Add empty values for the new column in all rows
    const updatedData = gridData.map(row => ({
      ...row,
      [newColumnId]: ''
    }));
    setGridData(updatedData);

    // Open the menu of the newly created column
    setTimeout(() => {
      openNewColumnMenu(newColumnId, updatedConfigs);
    }, 100);
  };

  // Duplicate the specified column
  const handleDuplicateColumn = (columnId: string | number) => {
    const targetColumn = columnConfigs.find(config => config.id === columnId);
    const targetIndex = columnConfigs.findIndex(config => config.id === columnId);
    
    if (!targetColumn || targetIndex === -1) return;

    const newColumnId = Math.max(...columnConfigs.map(c => Number(c.id))) + 1;
    
    // Create a copy of the target column with a new ID and modified name
    const duplicatedColumn: ColumnConfig = {
      id: newColumnId,
      name: targetColumn.name ? `Copy of ${targetColumn.name}` : '',
      options: { ...targetColumn.options }, // Deep copy of options
      width: targetColumn.width,
      hidden: targetColumn.hidden,
    };

    // Insert the duplicated column right after the original
    const updatedConfigs = [
      ...columnConfigs.slice(0, targetIndex + 1),
      duplicatedColumn,
      ...columnConfigs.slice(targetIndex + 1)
    ];
    setColumnConfigs(updatedConfigs);

    // Copy data from the original column to the new column
    const updatedData = gridData.map(row => ({
      ...row,
      [newColumnId]: row[columnId] || '' // Copy the data from the original column
    }));
    setGridData(updatedData);

    // Open the menu of the newly created column
    setTimeout(() => {
      openNewColumnMenu(newColumnId, updatedConfigs);
    }, 100);
  };

  // Helper function to open the menu of a newly created column
  const openNewColumnMenu = (columnId: string | number, configs?: ColumnConfig[]) => {
    const configsToUse = configs || columnConfigs;
    const visibleColumns = configsToUse.filter(config => !config.hidden);
    const columnIndex = visibleColumns.findIndex(config => config.id === columnId);
    
    if (columnIndex === -1) return;

    // Find the column header overlay item and click it to open the menu
    const overlayItems = document.querySelectorAll('.column-header-overlay-item');
    const targetOverlay = overlayItems[columnIndex] as HTMLElement;
    
    if (targetOverlay) {
      const headerContainer = targetOverlay.querySelector('.column-header-container') as HTMLElement;
      if (headerContainer) {
        headerContainer.click();
      }
    }
  };

  // Delete the specified column
  const handleDeleteColumn = (columnId: string | number) => {
    // Don't allow deleting if it's the last column
    if (columnConfigs.filter(config => !config.hidden).length <= 1) {
      return;
    }

    // Remove the column from configs
    const updatedConfigs = columnConfigs.filter(config => config.id !== columnId);
    setColumnConfigs(updatedConfigs);

    // Remove the column data from all rows
    const updatedData = gridData.map(row => {
      const { [columnId]: deletedColumn, ...remainingRow } = row;
      return remainingRow;
    });
    setGridData(updatedData);
  };

  // Handle column resize
  const handleColumnResize = (columnId: string | number, width: number) => {
    setColumnConfigs(prev => 
      prev.map(config => 
        config.id === columnId 
          ? { ...config, width }
          : config
      )
    );
  };

  // Convert data to ReactGrid format (simplified without checkboxes)
  const getRows = (data: CellData[]): Row<DefaultCellTypes | TextAreaCell>[] => {
    const visibleColumns = columnConfigs.filter(config => !config.hidden);
    const sortedData = getSortedData(data);
    
    return [
      {
        rowId: 'header',
        height: 34,
        cells: [
          { 
            type: 'header' as const, 
            text: 'ID',
            className: 'id-cell-center'
          },
          ...visibleColumns.map(config => ({
            type: 'header' as const,
            text: config.name,
            className: 'column-header-cell',
          })),
        ],
      },
      ...sortedData.map((rowData): Row<DefaultCellTypes | TextAreaCell> => ({
        rowId: rowData.id,
        height: 30,
        cells: [
          { 
            type: 'text' as const, 
            text: String(rowData.id), 
            nonEditable: true, 
            className: 'id-cell-center'
          },
          ...visibleColumns.map(config => ({
            type: 'textArea' as const,
            text: String(rowData[config.id] || ''),
            onRealTimeChange: (text: string) => {
              handleRealTimeChange(rowData.id, config.id, text);
            },
            onEnrichCell: () => {
              handleCellEnrich(Number(rowData.id), config.id);
            }
          } as TextAreaCell)),
        ],
      })),
    ];
  };

  // Handle cell changes with incremental updates
  const handleChanges = (changes: CellChange[]): void => {
    let shouldUpdateSelectedCell = false;
    let newSelectedCellContent = '';
    let editingCellUpdated = false;
    const currentSelectedCell = selectedCellRef.current;
    
    // Filter out only data cell changes for undo/redo tracking
    const dataChanges = changes.filter(change => 
      change.rowId !== 'header' && change.columnId !== 'id'
    );
    
    // Track cell changes for incremental save
    const newCellChanges: FirestoreCellChange[] = [];
    
    changes.forEach((change) => {
      if (change.rowId === 'header' && change.columnId !== 'id') {
        // Handle header cell changes (column name changes) - don't track in undo/redo
        setColumnConfigs(prev => {
          const updated = prev.map(config => 
            config.id === change.columnId 
              ? { ...config, name: (change.newCell as any).text || '' }
              : config
          );
          
          // Mark column configs as needing save
          setPendingColumnConfigChanges(updated);
          
          return updated;
        });
      } else if (change.rowId !== 'header' && change.columnId !== 'id') {
        
        // Create incremental cell change for Firestore
        const rowIndex = gridData.findIndex(row => row.id === change.rowId);
        if (rowIndex !== -1) {
          const cellChange = createCellChange(
            rowIndex,
            String(change.columnId),
            (change.newCell as any).text || '',
            (change.previousCell as any).text || ''
          );
          newCellChanges.push(cellChange);
        }
        
        // Check if this change affects the selected cell
        if (currentSelectedCell && currentSelectedCell.rowIndex === change.rowId && currentSelectedCell.columnId === change.columnId) {
          shouldUpdateSelectedCell = true;
          newSelectedCellContent = (change.newCell as any).text || '';
        }
        
        // Check if this was an editing cell that just finished editing
        if (editingCell && editingCell.rowIndex === change.rowId && editingCell.columnId === change.columnId) {
          editingCellUpdated = true;
        }
      }
    });
    
    // Add new cell changes to pending changes
    if (newCellChanges.length > 0) {
      setPendingCellChanges(prev => [...prev, ...newCellChanges]);
    }
    
    // Apply changes using undo/redo system for data changes
    if (dataChanges.length > 0) {
      setGridData((prevData) => applyChangesToData(dataChanges, prevData));
    }
    
    // Clear editing state if editing was completed
    if (editingCellUpdated) {
      setEditingCell(null);
    } else if (shouldUpdateSelectedCell && currentSelectedCell) {
      // Only update selected cell if we're not finishing editing
      const updatedCellInfo: SelectedCellInfo = {
        ...currentSelectedCell,
        content: newSelectedCellContent
      };
      setSelectedCell(updatedCellInfo);
      if (onCellSelected) {
        onCellSelected(updatedCellInfo);
      }
    }
  };

  // Function to start editing a specific cell
  const handleCellEdit = (cellInfo: SelectedCellInfo) => {
    if (!gridRef.current) {
      return;
    }

    try {
      const gridElement = gridRef.current;
      const allCells = gridElement.querySelectorAll('.rg-cell:not(.rg-header-cell)') as NodeListOf<HTMLElement>;
      
      // Calculate which cell should be focused based on row and column
      const rowIndex = gridData.findIndex(row => row.id === cellInfo.rowIndex);
      const visibleColumns = columnConfigs.filter(config => !config.hidden);
      const columnIndex = visibleColumns.findIndex(config => config.id === cellInfo.columnId);
      
      if (rowIndex !== -1 && columnIndex !== -1) {
        const cellIndex = rowIndex * visibleColumns.length + columnIndex;
        const targetCell = allCells[cellIndex];
        
        if (targetCell) {
          // First focus the cell
          targetCell.focus();
          targetCell.click();
          
          // Use Enter key to trigger edit mode (preserves content)
          setTimeout(() => {
            const enterEvent = new KeyboardEvent('keydown', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              bubbles: true,
              cancelable: true
            });
            targetCell.dispatchEvent(enterEvent);
          }, 100);
        }
      }
      
    } catch (error) {
      console.error('Error in handleCellEdit:', error);
    }
  };

  // Keyboard shortcuts handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
    
    // Check if focus is on our custom input fields
    const activeElement = document.activeElement;
    const isCustomInputFocused = activeElement && (
      (activeElement.tagName === 'INPUT' && activeElement.classList.contains('column-name-input')) ||
      (activeElement.tagName === 'TEXTAREA' && !activeElement.classList.contains('rg-textarea-editor')) ||
      ((activeElement as HTMLElement).isContentEditable && !activeElement.closest('.rg-cell')) ||
      (activeElement.getAttribute('contenteditable') === 'true' && !activeElement.closest('.rg-cell'))
    );
    
    if (isCtrlOrCmd) {
      switch (e.key.toLowerCase()) {
        case 'z':
          // Handle undo/redo for the grid
          if (e.shiftKey) {
            e.preventDefault();
            handleRedoChanges();
          } else {
            e.preventDefault();
            handleUndoChanges();
          }
          break;
        case 'y':
          // Ctrl+Y for redo (Windows)
          if (!isMac) {
            e.preventDefault();
            handleRedoChanges();
          }
          break;
      }
    } else if (e.key === 'Escape') {
      // Handle escape for clearing selection if not in a custom input field
      if (!isCustomInputFocused) {
        e.preventDefault();
        handleDeselectAll();
      }
    }
  };

  const rows = getRows(gridData);
  const columns = getColumns();

  // Additional logging for specific configuration aspects
  useEffect(() => {
    // Removed verbose logging
  }, [columnConfigs]);

  return (
    <div 
      className="data-grid-container" 
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="data-grid">
        <RowToolbar
          selectedCell={editingCell || selectedCell}
          rangeSelectionCount={rangeSelectionCount}
          onCellEdit={handleCellEdit}
          onAddColumn={handleAddColumn}
          onCopy={() => {}}
          onPaste={() => {}}
        />
        
        <div className="data-grid-content">
          <div ref={gridRef} style={{ position: 'relative' }}>
            {/* Column header menus - overlay approach */}
            <div className="column-headers-overlay">
              {columnConfigs.filter(config => !config.hidden).map((config, index) => {
                const visibleColumns = columnConfigs.filter(c => !c.hidden);
                // More precise calculation: ID column (50px) + sum of previous column widths + border adjustments
                const previousColumnsWidth = visibleColumns.slice(0, index).reduce((sum, col) => sum + col.width, 0);
                const leftOffset = 50 + previousColumnsWidth;
                
                // Get all column names for AI context (excluding current column)
                const allColumnNames = columnConfigs
                  .filter(col => col.id !== config.id && col.name.trim() !== '')
                  .map(col => col.name);
                
                return (
                  <div 
                    key={config.id} 
                    className="column-header-overlay-item"
                    style={{ 
                      left: `${leftOffset}px`,
                      width: `${config.width}px` 
                    }}
                    onMouseEnter={() => setHoveredColumnId(config.id)}
                    onMouseLeave={() => setHoveredColumnId(null)}
                  >
                    <ColumnHeaderMenu
                      columnId={config.id}
                      columnName={config.name}
                      options={config.options}
                      allColumnNames={allColumnNames}
                      onColumnNameChange={handleColumnNameChange}
                      onPromptDetailsChange={handlePromptDetailsChange}
                      onFormatDetailsChange={handleFormatDetailsChange}
                      onTypeChange={handleTypeChange}
                      onSortAscending={handleSortAscending}
                      onSortDescending={handleSortDescending}
                      onClearSort={handleClearSort}
                      onSelectColumn={(columnId) => {}}
                      onWrapText={handleWrapText}
                      onPinColumn={handlePinColumn}
                      onInsertColumnLeft={handleInsertColumnLeft}
                      onInsertColumnRight={handleInsertColumnRight}
                      onDuplicateColumn={handleDuplicateColumn}
                      onHideColumn={handleHideColumn}
                      onDeleteColumn={handleDeleteColumn}
                    />
                    <ColumnHoverButton
                      onEnrichRows={(rowCount) => handleColumnEnrich(config.id, rowCount)}
                      isVisible={hoveredColumnId === config.id && config.name.trim() !== '' && !isColumnEnriching(config.id)}
                      columnName={config.name}
                    />
                  </div>
                );
              })}
            </div>
            
            <ReactGrid
              rows={rows}
              columns={columns}
              onCellsChanged={handleChanges}
              onFocusLocationChanged={handleCellFocus}
              onColumnResized={handleColumnResize}
              enableRangeSelection
              stickyLeftColumns={1}
              customCellTemplates={{ 
                textArea: textAreaCellTemplate
              }}
            />
          </div>
        </div>
        
        <AddRowButton onAddRows={handleAddRows} />
      </div>
    </div>
  );
};

export default DataGrid; 